import { CreateOrder } from "~/types";
import ConnectionWrapper, {
    CHAIN_IDS,
    PROVIDER_ALLIANCE,
} from "./ConnectionWrapper";
import { EthereumProvider } from "@uxuycom/web3-tg-sdk/dist/types/src/provider/EthereumProvider/EthereumProvider";
import { transfer16 } from "~/utils/common";
import { getChainKey, getContract } from "./ChainKeyMaps";
import { fetchApproveResult } from "~/api/endpoints";

class EVMConnectionWrapper implements ConnectionWrapper {
    address: string | null = null;
    addressType = 1;
    chainId = String(CHAIN_IDS.ETH);
    alliance = PROVIDER_ALLIANCE.EVM;

    constructor(public provider: EthereumProvider) {
        this.address = this.provider.selectedAddress;
    }

    async getAddress(): Promise<string | null | undefined> {
        return this.provider
            .request({ method: "eth_accounts" })
            .then((accounts) => {
                const address = accounts[0];
                this.address = address;
                return address;
            })
            .catch((err) => {
                this.address = null;
                throw err;
            });
    }

    async getAddressSilently(): Promise<string | null | undefined> {
        this.address = this.provider.selectedAddress;
        return this.address;
    }

    async getChainId() {
        return this.provider.request({ method: "eth_chainId" });
    }

    async connect(): Promise<void> {
        return this.provider.enable();
    }

    isConnected(): boolean {
        return this.address != null && this.address.length > 0;
    }

    async disconnect(): Promise<void> {
        this.address = null;
        return await this.provider.disconnect();
    }

    async switchChain(chainId: string) {
        return this.provider
            .request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: transfer16(chainId), hideSwitch: true }],
            })
            .catch((error) => {
                if (error?.code == 4902) {
                    return this.provider.request({
                        method: "wallet_addEthereumChain",
                        params: [
                            {
                                hideSwitch: true,
                                chainId: transfer16(chainId),
                                chainName: transfer16(chainId),
                                iconUrls: [],
                                blockExplorerUrls: [],
                                rpcUrls: [],
                            },
                        ],
                    });
                } else {
                    console.error(error);
                    throw error;
                }
            });
    }

    async sendTransaction(
        chain_id: string,
        tradeOrder: CreateOrder
    ): Promise<any> {
        const currentChainId = await this.getChainId();

        if (String(currentChainId) !== String(chain_id)) {
            await this.switchChain(chain_id);
        }

        const transaction: any = {
            chainId: transfer16(chain_id),
            from: this.address,
            to: tradeOrder.to,
            nonce: tradeOrder.nonce,
            data: `0x${tradeOrder.transaction_data}`,
            gasPrice: transfer16(tradeOrder.gas_data.gas_price),
            gasLimit: transfer16(tradeOrder.gas_data.gas_limit),
            value: tradeOrder.value,
        };

        if (
            "max_priority_fee_per_gas" in tradeOrder.gas_data &&
            Number(tradeOrder.gas_data.max_priority_fee_per_gas) > 0 &&
            "max_fee_per_gas" in tradeOrder.gas_data &&
            Number(tradeOrder.gas_data.max_fee_per_gas) > 0
        ) {
            transaction.maxPriorityFeePerGas = transfer16(
                tradeOrder.gas_data.max_priority_fee_per_gas
            );
            transaction.maxFeePerGas = transfer16(
                tradeOrder.gas_data.max_fee_per_gas
            );
            delete transaction.gasPrice;
        }

        let options = {};

        const order_id = tradeOrder.order_id;

        if (tradeOrder.swap_info) {
            const chainId = tradeOrder.swap_info.from.chain_id;
            const chainKey = getChainKey(chainId);
            const fromTokenAddress = getContract(
                tradeOrder.swap_info.from.contract
            );
            const toTokenAddress = getContract(
                tradeOrder.swap_info.to.contract
            );

            options = {
                ext_payload: {
                    method: "wallet_tokenFollow",
                    params: [chainKey, [fromTokenAddress, toTokenAddress]],
                },
                ext_view_info: { ...tradeOrder.swap_info, order_id },
            };
        }

        return this.provider.request({
            method: "eth_sendTransaction",
            params: [transaction],
            options,
        });
    }

    async getTransactionByHash(hash: string) {
        try {
            // const receipt = await this.provider.request({
            //     method: "eth_getTransactionReceipt",
            //     params: [hash],
            // });
            // return receipt;
            const currentChainId = await this.getChainId();
            const result = await fetchApproveResult(
                Number(currentChainId).toString(10),
                hash
            );
            const status = result.data.status;
            if (status === 4) {
                return hash;
            } else {
                return null;
            }
        } catch (error) {
            return null;
        }
    }

    async waitForTransaction(
        hash: string,
        timeout = 1500,
        count = 3
    ): Promise<any> {
        const result = await this.getTransactionByHash(hash);
        if (result) return result;

        if (count <= 0) {
            return result;
        }

        await new Promise((resolve) => setTimeout(resolve, timeout));
        return this.waitForTransaction(hash, timeout, count - 1);
    }
}

export default EVMConnectionWrapper;
